
import { supportReadFromClipboardAPI } from 'bits/clipboard'
import { extractUrl } from 'bits/html_parser'
import { isUrl } from 'bits/url'
import { Component, Getter, namespace, Prop, State, Vue } from 'bits/vue'
import { SearchFilters } from 'bits/web_search'
import OzBackButton from 'library/v4/components/OzBackButton.vue'
import OzCloseButton from 'library/v4/components/OzCloseButton.vue'
import OzIconButton, { OzIconButtonColorScheme } from 'library/v4/components/OzIconButton.vue'
import OzTextButton, { OzTextButtonColorScheme } from 'library/v4/components/OzTextButton.vue'
import BaseIcon from 'vuecomponents/base_icon.vue'

// https://docs.microsoft.com/en-us/rest/api/cognitiveservices-bingsearch/bing-images-api-v5-reference
// The maximum URL length is 2,048 characters. To ensure that your URL length does not exceed the limit,
// the maximum length of your query parameters should be less than 1,500 characters.
// If the URL exceeds 2,048 characters, the server returns 404 Not found.
// Need to account for percent-encoded multi-code-point UTF-8 string.
const MAX_SEARCH_LENGTH = 1000

@Component({ components: { BaseIcon, OzBackButton, OzCloseButton, OzIconButton, OzTextButton } })
export default class ContentPickerSearchBar extends Vue {
  OzIconButtonColorScheme = OzIconButtonColorScheme
  OzTextButtonColorScheme = OzTextButtonColorScheme
  supportReadFromClipboardAPI = supportReadFromClipboardAPI

  $refs!: { searchBar: HTMLInputElement }
  searchBarFocusing = false
  MAX_SEARCH_LENGTH = MAX_SEARCH_LENGTH
  @State isSafari!: boolean
  @State((state) => state.isTouchable) isDeviceTouchable!: boolean
  @State isIPhone!: boolean
  @Getter dir!: string
  @namespace('browser').Getter isPhone!: boolean
  @namespace('browser').Getter isSmallPhone!: boolean
  @namespace('contentPicker').Getter searchFilter!: SearchFilters | null
  @namespace('contentPicker').Getter isError!: boolean
  @namespace('contentPicker').Action search!: (payload: { query: string }) => void
  @namespace('contentPicker').Action clearSearchError!: () => void
  @namespace('contentPicker').Action pickSearchFilter!: (payload: { filter: SearchFilters }) => void

  @Prop() xBackButton!: boolean
  @Prop() xClipboardButton!: boolean
  @Prop() value!: string
  @Prop() isLinkInputMode!: boolean
  @Prop() shouldWiggle!: boolean

  get searchBarPlaceholder(): string {
    if (this.isLinkInputMode) {
      return this.__('Paste or type link here')
    }
    if (this.isSmallPhone) return this.__('Search web or paste URL')
    return this.isPhone
      ? this.__('Search the web or paste URL')
      : this.__('Search images, GIFs, YouTube, Spotify, websites, or paste URL')
  }

  get searchQuery(): string {
    return this.$props.value
  }

  set searchQuery(val) {
    this.$emit('input', val)
  }

  handleCopyText(): void {
    this.$emit('copy')
  }

  handleSearchBarFocus(e): void {
    this.searchBarFocusing = true
    this.$emit('focus')
  }

  onInput(): void {
    if (this.isError) {
      this.clearSearchError()
    }
  }

  handleSearchBarFocusOut(): void {
    this.searchBarFocusing = false
    if (this.isIPhone) {
      this.processQuery()
    }
    this.$emit('blur')
  }

  mounted(): void {
    if (!this.isDeviceTouchable || this.isLinkInputMode) {
      // If it's touchable, keyboard will popup, we don't want that
      // still focus if we have opened the content picker in link input mode
      this.$refs.searchBar.focus()
    }
  }

  unfocusForTouchDevices() {
    if (this.isDeviceTouchable) {
      // If it's touchable, keyboard will popup, we don't want that
      this.$refs.searchBar.blur()
    }
  }

  focusSearchBar(): void {
    this.$refs.searchBar.focus()
  }

  select(data): void {
    this.$emit('select', data)
  }

  // If the search query is a link, use it as post attachment by calling this.select
  // Else use it to search with this.search
  // If we are in link input mode, default to web search
  processQuery(): void {
    const query = this.value
    this.clearSearchError()
    if (!query) {
      this.$emit('clear-search')
    } else if (isUrl(query)) {
      this.select(query)
    } else if (isUrl(`https://${query}`)) {
      this.select(`https://${query}`)
    } else {
      const extractedUrl = extractUrl(query)
      if (extractedUrl) {
        this.select(extractedUrl)
      } else {
        if (this.isLinkInputMode) {
          this.pickSearchFilter({ filter: SearchFilters.Web })
        }
        if (this.searchFilter === SearchFilters.None) {
          this.$emit('error', 'NO_SEARCH_FILTER')
          return
        }
        this.search({ query: query })
      }
    }
    this.unfocusForTouchDevices()
  }

  resetSearch(): void {
    this.$emit('clear-search')
    this.$refs.searchBar.focus()
  }
}
